import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, Controller, useWatch } from "react-hook-form";
import { FmAutocompleteInput, FmButton, FmLoader, FmMessage, FmTextField, FmUnderlineNav } from "../../Components";
import { ValidationUtils } from "../../Utils";
import { useIsMobile } from "../../Hooks";
import { useDispatch, useSelector } from "react-redux";
import { AuthState, GeneralState, ContactUsState } from "../../Redux";
import { useHistory } from "react-router-dom";
import useStyles from "./ContactUsPage.styles";
import { ContactUsRequest } from "../../Models/ServerModels/ContactUs/ContactUsRequest";
import { TextField } from "@mui/material";

interface IFormInputs {
    country: string;
    region: string | undefined;
    position: string;
    growingMethod: string;
    cellPhoneNumber: string;
    areaSize: string;
    emailAddress: string;
    yourMessage: string;
}

const ContactUsPage = () => {
    const [toggleCountryUpdate, setToggleCountryUpdate] = useState<boolean>(false);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const classes = useStyles();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const history = useHistory();
    const dispatch = useDispatch();
    const userDetail = useSelector(GeneralState.Selectors.selectUser);
    const name = userDetail?.name;

    const contactUsInProgress = useSelector(ContactUsState.Selectors.selectContactUsIsLoading);
    const contactUsMessage = useSelector(GeneralState.Selectors.selectSummaryDescription);
    const user = useSelector(GeneralState.Selectors.selectUserDetails);

    const contactUsErrorMessage = useSelector(ContactUsState.Selectors.selectContactUsErrorMessage);

    const { countries, regionsByCountry, specializations } = useSelector(AuthState.Selectors.selectRegistrationData);
    const {
        handleSubmit,
        control,
        register,
        getValues,
        setValue,
        watch,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: "onSubmit",
        defaultValues: {
            country: userDetail?.country,
            region: userDetail?.region,
            emailAddress: userDetail?.email,
            position: userDetail?.expertise,
        },
    });

    const goBackHandler = () => {
        history.goBack();
    };
    useEffect(() => {
        (window as any).dataLayer.push({
            event: "ContactUs",
            pageUrl: "https://multimatch.haifa-group.com/multi-match/contact-us",
            pageTitle: "ContactUs",
        });
    }, []);
    useEffect(() => {
        const contactUsBody: ContactUsRequest = {
            fullName: name as string,
            country: getValues("country"),
            region: getValues("region"),
            mobilePhone: getValues("cellPhoneNumber"),
            email: getValues("emailAddress"),
            expertise: getValues("position"),
            description: getValues("yourMessage"),
            url: "",
            mainInterest: user ? user.mainInterest : "OFA",
        };

        return () => {
            const sendContact = async () => {
                await dispatch(ContactUsState.Actions.setContactUsFormData(contactUsBody));
            };
            sendContact();
        };
    }, [dispatch]);

    const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
        const contactUsBody = {
            fullName: name as string,
            country: data.country,
            region: data.region,
            mobilePhone: data.cellPhoneNumber,
            email: data.emailAddress,
            expertise: data.position,
            description: data.yourMessage,
            url: "",
            mainInterest: user ? user.mainInterest : "OFA",
        };

        await dispatch(ContactUsState.Thunks.sendContactUsLead(contactUsBody));

        !contactUsErrorMessage && !contactUsInProgress && setShowSuccessMessage(true);
        contactUsErrorMessage && !contactUsInProgress && setShowErrorMessage(true);
    };

    const regions = useMemo(() => {
        const country = getValues("country");
        const countriesRegions = regionsByCountry[country];
        return countriesRegions || [];
    }, [regionsByCountry, toggleCountryUpdate, getValues]);

    const closeMessageHandler = () => {
        setShowSuccessMessage(false);
    };

    const closeErrorMessageHandler = () => {
        setShowErrorMessage(false);
    };

    useEffect(() => {
        contactUsMessage && setValue("yourMessage", contactUsMessage);
    }, [contactUsMessage]);

    return (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
            <div className={isMobile ? classes.mobile_container : classes.container}>
                <div className={isMobile ? classes.mobile_title : classes.title}>{t("contactUs.title")}</div>
                <div className={isMobile ? classes.mobile_secondTitle : classes.secondTitle}>{`${name} ${t("contactUs.secondTitle")}`}</div>
                <div className={isMobile ? classes.mobile_secondTitle : classes.secondTitle}>{t("contactUs.description")}</div>
                <div className={isMobile ? classes.mobile_inputs_container : classes.inputs_container}>
                    <div className={isMobile ? classes.mobile_singleRow_container : classes.singleRow_container}>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"country"}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmAutocompleteInput
                                            {...register("country")}
                                            {...field}
                                            label={t("contactUs.inputLabel1")}
                                            error={errors.country}
                                            helperText={errors.country?.message || ""}
                                            data={countries}
                                            getLabel={(country: any) => t(country.code ? `countries.${country.code}` : `countries.${country}`)}
                                            setValue={(country: any) => {
                                                setToggleCountryUpdate((update) => !update);
                                                setValue("country", country ? country.code : undefined);
                                                setValue("region", undefined);
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"region"}
                                control={control}
                                key={regions && regions[0] && regions[0].countryCode}
                                render={({ field }) => {
                                    return (
                                        <FmAutocompleteInput
                                            {...register("region")}
                                            {...field}
                                            label={t("contactUs.inputLabel2")}
                                            error={errors.region}
                                            helperText={errors.region?.message || ""}
                                            data={regions}
                                            getLabel={(region: any) => t(region.code ? `regions.${region.code}` : `regions.${region}`)}
                                            setValue={(region: any) => {
                                                setValue("region", region ? region.code : undefined);
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                    </div>
                    {/* --------------------------- second row ------------------------------- */}
                    <div className={isMobile ? classes.mobile_singleRow_container : classes.singleRow_container}>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"position"}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmAutocompleteInput
                                            {...register("position")}
                                            {...field}
                                            label={t("contactUs.inputLabel3")}
                                            error={errors.position}
                                            helperText={errors.position?.message || ""}
                                            data={specializations}
                                            getLabel={(specializations: any) => {
                                                return t(specializations.code ? `specialties.${specializations.code}` : `specialties.${specializations}`);
                                            }}
                                            setValue={(specializations: any) => {
                                                setValue("position", specializations && specializations.code ? specializations.code : undefined);
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"cellPhoneNumber"}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                    minLength: {
                                        value: 9,
                                        message: t("contactUs.messages.invalidPhoneNumber"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmTextField
                                            {...register("cellPhoneNumber")}
                                            {...field}
                                            label={t("contactUs.inputLabel5")}
                                            variant='filled'
                                            type='number'
                                            fullWidth='100%'
                                            error={errors.cellPhoneNumber}
                                            value={field.value}
                                            helperText={errors.cellPhoneNumber?.message || ""}
                                        />
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div className={isMobile ? classes.mobile_singleRow_container : classes.singleRow_container}>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"emailAddress"}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                    pattern: {
                                        value: ValidationUtils.StringRegex.EMAIL,
                                        message: t("validationErrors.incorrectEmailFormat"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmTextField
                                            {...register("emailAddress")}
                                            {...field}
                                            label={t("contactUs.inputLabel7")}
                                            variant='filled'
                                            type='email'
                                            fullWidth='100%'
                                            error={errors.emailAddress}
                                            value={field.value}
                                            helperText={errors.emailAddress?.message || ""}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classes.inputWrapper}>
                            <Controller
                                name={"yourMessage"}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmTextField
                                            {...register("yourMessage")}
                                            {...field}
                                            label={t("contactUs.inputLabel8")}
                                            variant='filled'
                                            type='text'
                                            fullWidth='100%'
                                            error={errors.yourMessage}
                                            value={field.value}
                                            helperText={errors.yourMessage?.message || ""}
                                            multiline
                                            minRows={7}
                                            maxRows={10}
                                            textArea={true}
                                        />
                                    );
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={isMobile ? classes.mobile_buttons_container : classes.buttons_container}>
                    {!isMobile && <FmUnderlineNav title={t("contactUs.back")} onClick={goBackHandler} />}
                    <FmButton fullWidth={isMobile ? "fullWidth" : "regular"} loading={contactUsInProgress} disabled={contactUsInProgress}>
                        {t("contactUs.send")}
                    </FmButton>
                </div>
            </div>
            {!isMobile && (
                <div className={classes.leafPage}>
                    <img src='./assets/leafsPage.png' alt='leaf' />
                </div>
            )}
            {showErrorMessage && (
                <FmMessage className={classes.message} variant={"failure"} closeMessage={closeErrorMessageHandler} snackbar={true}>
                    {contactUsErrorMessage || "Something went wrong, Please try again later.."}
                </FmMessage>
            )}
            {showSuccessMessage && (
                <FmMessage className={classes.message} variant={"success"} closeMessage={closeMessageHandler} snackbar={true}>
                    {!contactUsInProgress && !contactUsErrorMessage && t("contactUs.messages.success")}
                </FmMessage>
            )}
        </form>
    );
};

export default ContactUsPage;
